import type { TreeNode } from '@morph-mapper/types';
import { definePlugin } from '../../framework';
import { FieldSuggestion } from '../../types';
import { isNumericIntentField } from '../number-handling';
import type { IteratorStrategy, UtilsApi } from '../../types';

type InitParams = { initialFields: string[] };

const MIN_SCORE_THRESHOLD = 50;

const THRESHOLD_EXEMPT_FIELDS: string[] = [
  '#iterator',
  'loadingtimereadable',
  'dischargetimereadable',
  'loadingdriverinstructions',
  'checking',
  'selector',
].map((f) => f.trim().toLowerCase());

export async function scoreSuggestion(
  s: FieldSuggestion,
  callRender: UtilsApi['callRender'],
): Promise<{ score: number; rendered?: string }> {
  let snippetForScoring = s.snippet;
  const value = s.value;

  // These override checks must run before the value-shape guard below —
  // a verified/cross-field suggestion can legitimately carry an empty or
  // non-string value (e.g. declare-items sets verified:true on options
  // whose value already collapsed to '') and must not be short-circuited
  // to score -50 before ever reaching its intended branch.
  if ((s as any).verified === true) {
    return { score: 100 };
  }

  if ((s as any).crossField === true) {
    return {
      score: 25,
      rendered: typeof s.value === 'string'
        ? s.value
        : String(s.value)
    };
  }

  if (typeof value !== 'string' || value.length === 0) {
    return { score: -50 };
  }

  if (
    typeof snippetForScoring !== 'string' &&
    !Array.isArray(snippetForScoring)
  ) {
    return { score: -50 };
  }
  if (Array.isArray(snippetForScoring)) {
    if (
      Array.isArray(snippetForScoring[0]) &&
      snippetForScoring[0].length > 0
    ) {
      snippetForScoring = snippetForScoring[0][0];
    }
  }

  const snippetStr = typeof snippetForScoring === 'string'
    ? snippetForScoring
    : JSON.stringify(snippetForScoring);

  const isColumnSnippet = /^#>\d+\$get$/.test(snippetStr) || /^#>\$column#\d+$/.test(snippetStr);

  if (isColumnSnippet) {
    return { score: 50 };
  }

  try {
    const renderSnippet = typeof snippetForScoring === 'string'
      ? JSON.stringify(snippetForScoring.replace(/<MS>/g, '  '))
      : JSON.stringify(snippetForScoring);
    const result = await callRender(renderSnippet);

    if (!result.isOk()) return { score: -25 };

    const rendered = String(result.value);

    if (typeof rendered !== 'string') return { score: -50 };

    const trimmedRendered = rendered.trim().toLowerCase();
    const trimmedValue = value.replace(/^c\d+:\s*/i, '').trim().toLowerCase();

    let score: number;

    if (trimmedRendered === trimmedValue) {
      score = 100;
    } else if (trimmedRendered.includes(trimmedValue)) {
      const ratio = trimmedValue.length / trimmedRendered.length;
      score = Math.floor(ratio * 90);
    } else {
      const minLength = Math.min(trimmedRendered.length, trimmedValue.length);
      let sameChars = 0;

      for (let i = 0; i < minLength; i++) {
        if (trimmedRendered.charAt(i) === trimmedValue.charAt(i)) sameChars++;
      }

      const similarityRatio = sameChars / trimmedValue.length;
      score = Math.floor(similarityRatio * 50);
    }

    const excessRatio = Math.max(0,
      (trimmedRendered.length - trimmedValue.length) /
      Math.max(trimmedValue.length, 1)
    );
    const lengthPenalty = Math.min(90, Math.floor(excessRatio * 30));
    const finalScore = Math.max(-25, score - lengthPenalty);
    return { score: finalScore, rendered };
  } catch (error) {
    console.warn(
      `[SortSuggestions] callRender failed`,
      error
    );
    return { score: -100 };
  }
}

const isColumnValue = (value: unknown): boolean =>
  typeof value === 'string' && /^c\d+:\s*/i.test(value);

export function keepScoredSuggestions<
  T extends { value: unknown; score?: number },
>(scored: T[], entry: TreeNode, iteratorStrategy?: IteratorStrategy): T[] {
  const fieldKeyNormalized = String(entry.key).trim().toLowerCase();
  const isExempt =
    THRESHOLD_EXEMPT_FIELDS.includes(fieldKeyNormalized) || isNumericIntentField(entry);

  const columnSuggestions = scored.filter((s) => isColumnValue(s.value));
  const nonColumnSuggestions = scored.filter((s) => !isColumnValue(s.value));
  const useColumnOnly = iteratorStrategy === 'table' && columnSuggestions.length > 0;

  const columnScoped = useColumnOnly
    ? columnSuggestions
    : iteratorStrategy === 'countOccurrence'
      ? nonColumnSuggestions
      : scored;

  // Cross-field suggestions are intentionally scored below the threshold
  // (they're a lower-confidence, non-autofill fallback) but should still
  // reach the UI as a selectable option rather than being silently pruned.
  return columnScoped.filter(
    (s) =>
      isExempt ||
      useColumnOnly ||
      (s as any).crossField === true ||
      (s.score ?? 0) >= MIN_SCORE_THRESHOLD,
  );
}

export const sortSuggestions = definePlugin<{}>({
  name: 'Sort Suggestions',
  version: '0.0.1',
  initialState: {},

  bootstrap: () => {},

  async postFieldSuggestions({ entry, suggestions, iteratorStrategy, callRender }) {
    if (!suggestions || suggestions.length === 0) {
      console.warn(`[SortSuggestions] No suggestions for "${entry.key}"`);
      return suggestions;
    }

    const scoredSuggestions = [];

    for (const s of suggestions) {
      const { score, rendered } = await scoreSuggestion(s, callRender);

      scoredSuggestions.push({
        ...s,
        score,
        rendered,
      });
    }

    const sorted = [...scoredSuggestions].sort((a, b) => {
      if ((a.score ?? 0) > (b.score ?? 0)) return -1;
      if ((a.score ?? 0) < (b.score ?? 0)) return 1;
      return 0;
    });

    const filtered = keepScoredSuggestions(sorted, entry, iteratorStrategy);

    suggestions.length = 0;
    suggestions.push(...filtered);

    return suggestions;
  },
});
